//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useEffect, useContext } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Autocomplete,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { Add, Delete, ExpandLess, ExpandMore } from '@mui/icons-material';

// === COMPONENTS ===
import { Spacer            } from 'components/Spacer';
import { ExpressionContext } from '../../../../../Context/ExpressionContext';
import { ColumnMapList     } from './components/ColumnMap/ColumnMapList';

// === UTILS ===
import { getHexAlpha     } from 'components/styles.utils';
import { sendMessageData } from 'api/prokey/prokey.websocket';

//#endregion

//-----------------------------------------------------------------
export function TablesMatchControl( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { data } = props;

  const _source_items = [
    { label: 'Result'  , value: 'RES' },
    { label: 'Record'  , value: 'DOR' },
  ]

  const { updateExpression, tables, table_columns } = useContext( ExpressionContext );

  const [ table1_source , setTable1Source ] = useState( _source_items[0].value );
  const [ table1_option , setTable1Option ] = useState( tables.find( x=> x.value === data?.table1?.name ) );
  const [ table1_value  , setTable1Value  ] = useState( data?.table1?.name );
  const [ table2_source , setTable2Source ] = useState( _source_items[1].value );
  const [ table2        , setTable2       ] = useState( data?.table2?.name );
  const [ map           , setMap          ] = useState( {} );
  const [ is_open       , setIsOpen       ] = useState( false );

  const expand_icon = is_open ? <ExpandLess /> : <ExpandMore />;

  //#endregion

  //#region [STYLES]

  const theme = useTheme();
  const _styles = {
    select_field: {
      width: '40rem',
    },
    select_column: {
      width: '30rem',
    },
    text_table2: {
      width: '40rem',
    },
    button_add: {
      backgroundColor: theme.palette.primary[theme.palette.mode],
      color: 'white'
    },
    button_expand: {
      width  : '3rem',
      height : '3rem',
    },
  }

  const SELECT_SOURCE_1 = {
    sx       : _styles.select_source,
    labelId  : 'source-label-1',
    id       : 'source-select-1',
    key      : 'table-1-source',
    label    : 'Source',
    value    : table1_source,
    size     : 'small',
    disabled : true,
    onChange : onChange_Source1,
    children : _source_items.map( item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)
  };

  const SELECT_SOURCE_2 = {
    sx       : _styles.select_source,
    labelId  : 'source-label-2',
    id       : 'source-select-2',
    key      : 'table-2-source',
    label    : 'Source',
    value    : table2_source,
    size     : 'small',
    disabled : true,
    onChange : onChange_Source2,
    children : _source_items.map( item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)
  };
    
  const SELECT_TABLE_1 = {
    sx          : _styles.select_field,
    id          : 'table-select',
    key         : 'table-1-select',
    options     : tables,
    value       : table1_option,
    inputValue  : table1_value ,
    size        : 'small',
    onChange      : onChange_Table1,
    onInputChange : onChange_Table1Input,
    renderInput : (params) => <TextField {...params} label="Table" />,
    getOptionLabel : (option) => option.label,
    disableClearable : true,
  };

  const TEXT_TABLE_2 = {
    sx    : _styles.text_table2,
    key   : 'table-2',
    value : table2,
    size  : 'small',
    onChange  : onChange_Table2  ,
    onKeyDown : onKeyDown_Table2 ,
  };

  const BUTTON_EXPAND = {
    sx: _styles.button_expand,
    key : 'expand',
    children: expand_icon,
    onClick: () => { setIsOpen(!is_open) }
  };
    
  //#endregion


  //#region [EFFECT]

  useEffect( () => {
    sendMessageData( 'TABLE-COLUMNS-REQUEST', { table_name: table1_value } )
  }, [table1_value] );

  //#endregion

  //#region [EVENTS]
  
  //-----------------------------------------------------------------
  function onChange_Source1( event ) {
    //-----------------------------------------------------------------
    setTable1Source( event.target.value );
    updateExpression( props.id , { table1: { source: event.target.value, name: table1_value } } )
  }
  
  //-----------------------------------------------------------------
  function onChange_Table1( event, value ) {
    //-----------------------------------------------------------------
    setTable1Option( value );
  }
  
  //-----------------------------------------------------------------
  function onChange_Table1Input( event, value ) {
    //-----------------------------------------------------------------
    setTable1Value( value );
    updateExpression( props.id , { table1: { source: table1_source, name: value } } )
  }
  
  //-----------------------------------------------------------------
  function onChange_Source2( event ) {
    //-----------------------------------------------------------------
    setTable2Source( event.target.value );
    updateExpression( props.id , { table2: { source: event.target.value, name: table2 } } )
  }
  
  //-----------------------------------------------------------------
  function onChange_Table2( event, value ) {
    //-----------------------------------------------------------------
    setTable2( event.target.value );
    // updateExpression( props.id , { table1: { source: table2_source, name: event.target.value } } )
    // setTable2( value );
    // updateExpression( props.id , { table1: { source: table2_source, name: value.value } } )
  }
  
  //-----------------------------------------------------------------
  function onKeyDown_Table2( event ) {
    //-----------------------------------------------------------------
    if (event.key === 'Enter') {
      updateExpression( props.id , { table2: { source: table2_source, name: event.target.value } } );
    }
  }

  function handleColumnMapChange( map ) {
    updateExpression( props.id, { map } );
  }

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <React.Fragment key={props.id}>
        <div style={{display: 'flex'}}>
          <div>
            <div style={{display: 'flex'}}>
              {/** Table 1 */}
              <FormControl>
                <InputLabel id="source-label-1">Source</InputLabel>
                <Select   { ...SELECT_SOURCE_1 } />
              </FormControl>
              <Spacer/>
              <Autocomplete { ...SELECT_TABLE_1 } />
              <Spacer />
              {/** Table 2 */}
              <FormControl>
                <InputLabel id="source-label-2">Source</InputLabel>
                <Select   { ...SELECT_SOURCE_2 } />
              </FormControl>
              <Spacer />
              <TextField { ...TEXT_TABLE_2 } />
            </div>
            <div style={{ display: is_open ? '' : 'none' }}>
              <ColumnMapList data={data.map} columns1={table_columns[table1_value]} columns2={table_columns[table2]} onMapChange={handleColumnMapChange} />
            </div>
          </div>
          <Spacer />
          <IconButton { ...BUTTON_EXPAND } />
        </div>
      </React.Fragment>
    );
  }



  //#endregion

  return display();
}